import React, { useState, useEffect, useCallback } from 'react';
import api from '../../lib/api';
import { useAuth } from '../../contexts/AuthContext';
import { accessIntentForStatus, ACCESS_INTENT, PROJECT_ROLE, GRANTABLE_PROJECT_ROLES } from '../../lib/auth';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import {
  Table,
  TableHeader,
  TableBody,
  TableHead,
  TableRow,
  TableCell,
} from '../ui/table';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';
import { Users, Plus, Trash2, Loader2, UserPlus } from 'lucide-react';
import { toast } from 'sonner';

// Grantable designations (RBAC v2.1): view, collaborate, AND plan (co-planner).
// PLAN is offered ONLY for a user who holds the org planner grant (is_planner) —
// the API rejects granting plan to a non-planner. The OWNER's own row is never
// edited here (it renders an immutable "Owner" badge).
const ROLE_LABELS = {
  [PROJECT_ROLE.VIEW]: 'View',
  [PROJECT_ROLE.COLLABORATE]: 'Collaborate',
  [PROJECT_ROLE.PLAN]: 'Planner',
};
const ALL_ROLE_OPTIONS = GRANTABLE_PROJECT_ROLES.map((value) => ({
  value,
  label: ROLE_LABELS[value] || value,
}));
const roleLabel = (role) => ROLE_LABELS[role] || role;
// Options a given user may be granted: plan only if they are an org planner.
const roleOptionsFor = (isPlanner) =>
  ALL_ROLE_OPTIONS.filter((opt) => opt.value !== PROJECT_ROLE.PLAN || isPlanner);
// As above, but always keep the member's CURRENT role selectable (so a co-planner
// whose org grant was later revoked still shows 'plan' and can be demoted).
const roleOptionsForMember = (member) => {
  const opts = roleOptionsFor(member.is_planner);
  if (!opts.some((o) => o.value === member.role)) {
    opts.push({ value: member.role, label: roleLabel(member.role) });
  }
  return opts;
};

const ROLE_BADGE_CLASS = {
  [PROJECT_ROLE.VIEW]: 'bg-slate-100 text-slate-700',
  [PROJECT_ROLE.COLLABORATE]: 'bg-blue-100 text-blue-700',
  [PROJECT_ROLE.PLAN]: 'bg-emerald-100 text-emerald-700',
};

const errorMessage = (error, fallback) =>
  error?.response?.data?.detail || fallback;

/**
 * ProjectMembersPanel — in-project member management for one project (RBAC v2.1).
 * Drives GET/POST/PATCH/DELETE /api/projects/{pid}/members.
 *
 * Render this only when can(Action.MANAGE_MEMBERS, projectRole) — the owner, a
 * co-planner (project role 'plan'), or super_admin. Callers gate it; the
 * component itself trusts that gate but still degrades correctly:
 *   - 404 from the list endpoint => no access to this project => HIDE (render null)
 *   - 403 from the list endpoint => visible but every control disabled
 *
 * Only the OWNER's row (member.user_id === ownerId) is immutable — an "Owner"
 * badge, no picker, no remove (ownership moves only via owner reassignment).
 * Co-planners (role 'plan' but NOT the owner) are ordinary editable/removable
 * rows. The 'plan' (co-planner) option is offered only for users who hold the
 * org planner grant (member.is_planner).
 *
 * @param {{ projectId: string, ownerId?: string|null }} props
 */
const ProjectMembersPanel = ({ projectId, ownerId = null }) => {
  const { user } = useAuth();

  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  // 'allow' | 'hide' | 'disable' — derived from the list endpoint's status.
  const [intent, setIntent] = useState(ACCESS_INTENT.ALLOW);

  // Add-member dialog state.
  const [showAdd, setShowAdd] = useState(false);
  const [orgUsers, setOrgUsers] = useState([]);
  const [selectedUserId, setSelectedUserId] = useState('');
  const [selectedRole, setSelectedRole] = useState(PROJECT_ROLE.VIEW);
  const [adding, setAdding] = useState(false);

  // Per-row in-flight flags so a single row's spinner/disabled state is isolated.
  const [rowBusy, setRowBusy] = useState({});

  const disabled = intent === ACCESS_INTENT.DISABLE;

  // Add-dialog role options gate 'plan' (co-planner) on the selected user's
  // org planner grant — mirrors the API precondition.
  const selectedUser = orgUsers.find((u) => u.id === selectedUserId);
  const addRoleOptions = roleOptionsFor(!!selectedUser?.is_planner);

  const fetchMembers = useCallback(async () => {
    if (!projectId) return;
    setLoading(true);
    try {
      const res = await api.get(`/projects/${projectId}/members`);
      setMembers(res.data || []);
      setIntent(ACCESS_INTENT.ALLOW);
    } catch (error) {
      const status = error?.response?.status;
      const next = accessIntentForStatus(status);
      setIntent(next);
      if (next === ACCESS_INTENT.DISABLE) {
        toast.error(errorMessage(error, 'You cannot manage members on this project'));
      }
      // 404 (HIDE) is silent — the panel simply renders null.
    } finally {
      setLoading(false);
    }
  }, [projectId]);

  useEffect(() => {
    fetchMembers();
  }, [fetchMembers]);

  // Users available to invite — the owner-gated endpoint (a planner cannot
  // call the admin-only /admin/users). Server already excludes existing
  // members and the owner.
  const loadOrgUsers = useCallback(async () => {
    try {
      const res = await api.get(`/projects/${projectId}/members/invitable`);
      setOrgUsers(res.data || []);
    } catch (error) {
      toast.error(errorMessage(error, 'Failed to load users'));
    }
  }, [projectId]);

  const openAddDialog = async () => {
    setSelectedUserId('');
    setSelectedRole(PROJECT_ROLE.VIEW);
    await loadOrgUsers();
    setShowAdd(true);
  };

  const handleAdd = async () => {
    if (!selectedUserId) {
      toast.error('Select a user to add');
      return;
    }
    setAdding(true);
    try {
      await api.post(`/projects/${projectId}/members`, {
        user_id: selectedUserId,
        role: selectedRole,
      });
      toast.success('Member added');
      setShowAdd(false);
      await fetchMembers();
    } catch (error) {
      toast.error(errorMessage(error, 'Failed to add member'));
    } finally {
      setAdding(false);
    }
  };

  const handleRoleChange = async (memberUserId, role) => {
    setRowBusy((prev) => ({ ...prev, [memberUserId]: true }));
    try {
      const res = await api.patch(
        `/projects/${projectId}/members/${memberUserId}`,
        { role }
      );
      setMembers((prev) =>
        prev.map((m) => (m.user_id === memberUserId ? { ...m, role: res.data.role } : m))
      );
      toast.success('Role updated');
    } catch (error) {
      toast.error(errorMessage(error, 'Failed to update role'));
      // Re-sync from server so the Select does not show a stale optimistic value.
      await fetchMembers();
    } finally {
      setRowBusy((prev) => ({ ...prev, [memberUserId]: false }));
    }
  };

  const handleRemove = async (memberUserId) => {
    setRowBusy((prev) => ({ ...prev, [memberUserId]: true }));
    try {
      await api.delete(`/projects/${projectId}/members/${memberUserId}`);
      setMembers((prev) => prev.filter((m) => m.user_id !== memberUserId));
      toast.success('Member removed');
    } catch (error) {
      toast.error(errorMessage(error, 'Failed to remove member'));
    } finally {
      setRowBusy((prev) => ({ ...prev, [memberUserId]: false }));
    }
  };

  // 404 => caller has no access to this project: pretend the panel isn't here.
  if (intent === ACCESS_INTENT.HIDE) {
    return null;
  }

  return (
    <Card className="border-slate-200" data-testid="project-members-panel">
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <CardTitle className="text-base font-semibold text-slate-900 flex items-center gap-2">
            <Users className="h-4 w-4 text-slate-500" />
            Project Members
          </CardTitle>
          <Button
            size="sm"
            className="h-8 gap-2 bg-slate-900 hover:bg-slate-800"
            onClick={openAddDialog}
            disabled={disabled || loading}
            data-testid="add-member-button"
          >
            <Plus className="h-4 w-4" />
            Add Member
          </Button>
        </div>
      </CardHeader>
      <CardContent>
        {loading ? (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="h-6 w-6 animate-spin text-slate-400" />
          </div>
        ) : members.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-8 text-center">
            <div className="w-12 h-12 rounded-full bg-slate-100 flex items-center justify-center mb-3">
              <UserPlus className="h-6 w-6 text-slate-300" />
            </div>
            <p className="text-sm font-medium text-slate-900 mb-1">No members yet</p>
            <p className="text-xs text-slate-400">
              Add a member to grant project access
            </p>
          </div>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Name</TableHead>
                <TableHead>Email</TableHead>
                <TableHead className="w-[180px]">Project Role</TableHead>
                <TableHead className="w-[80px] text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {members.map((member) => {
                const busy = !!rowBusy[member.user_id];
                const isSelf = member.user_id === String(user?.id);
                // ONLY the owner_id row is the protected owner. A 'plan' member
                // who is NOT the owner is a co-planner — an ordinary editable row.
                const isOwner = !!ownerId && member.user_id === String(ownerId);
                return (
                  <TableRow key={member.user_id} data-testid={`member-row-${member.user_id}`}>
                    <TableCell className="font-medium text-slate-900">
                      {member.name || '—'}
                      {isSelf && (
                        <Badge variant="outline" className="ml-2 text-[10px]">
                          You
                        </Badge>
                      )}
                    </TableCell>
                    <TableCell className="text-slate-500">{member.email || '—'}</TableCell>
                    <TableCell>
                      {isOwner ? (
                        <Badge
                          className={`${ROLE_BADGE_CLASS[PROJECT_ROLE.PLAN]} text-[10px]`}
                          data-testid={`member-role-${member.user_id}`}
                        >
                          Owner
                        </Badge>
                      ) : (
                        <Select
                          value={member.role}
                          onValueChange={(role) => handleRoleChange(member.user_id, role)}
                          disabled={disabled || busy}
                        >
                          <SelectTrigger
                            className="h-8"
                            data-testid={`member-role-${member.user_id}`}
                          >
                            <SelectValue>
                              <Badge
                                className={`${ROLE_BADGE_CLASS[member.role] || ''} text-[10px]`}
                              >
                                {roleLabel(member.role)}
                              </Badge>
                            </SelectValue>
                          </SelectTrigger>
                          <SelectContent>
                            {roleOptionsForMember(member).map((opt) => (
                              <SelectItem key={opt.value} value={opt.value}>
                                {opt.label}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      )}
                    </TableCell>
                    <TableCell className="text-right">
                      {!isOwner && (
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-8 w-8 p-0 text-slate-400 hover:text-red-600"
                          onClick={() => handleRemove(member.user_id)}
                          disabled={disabled || busy}
                          data-testid={`remove-member-${member.user_id}`}
                          aria-label="Remove member"
                        >
                          {busy ? (
                            <Loader2 className="h-4 w-4 animate-spin" />
                          ) : (
                            <Trash2 className="h-4 w-4" />
                          )}
                        </Button>
                      )}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        )}
      </CardContent>

      {/* Add Member Dialog */}
      <Dialog open={showAdd} onOpenChange={setShowAdd}>
        <DialogContent className="sm:max-w-[420px]">
          <DialogHeader>
            <DialogTitle className="text-lg font-semibold text-slate-900">
              Add Project Member
            </DialogTitle>
            <DialogDescription className="text-sm text-slate-500">
              Grant a user a role on this project.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <label className="text-sm font-medium text-slate-900">User</label>
              <Select
                value={selectedUserId}
                onValueChange={(v) => {
                  setSelectedUserId(v);
                  setSelectedRole(PROJECT_ROLE.VIEW); // reset; 'plan' may not be valid for the new user
                }}
              >
                <SelectTrigger className="h-9" data-testid="add-member-user-select">
                  <SelectValue placeholder="Select a user" />
                </SelectTrigger>
                <SelectContent>
                  {orgUsers.length === 0 ? (
                    <div className="px-2 py-1.5 text-sm text-slate-400">
                      No users available
                    </div>
                  ) : (
                    orgUsers.map((u) => (
                      <SelectItem key={u.id} value={u.id}>
                        {u.name} ({u.email})
                      </SelectItem>
                    ))
                  )}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <label className="text-sm font-medium text-slate-900">Project Role</label>
              <Select value={selectedRole} onValueChange={setSelectedRole}>
                <SelectTrigger className="h-9" data-testid="add-member-role-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {addRoleOptions.map((opt) => (
                    <SelectItem key={opt.value} value={opt.value}>
                      {opt.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => setShowAdd(false)}
              disabled={adding}
            >
              Cancel
            </Button>
            <Button
              type="button"
              className="bg-slate-900 hover:bg-slate-800"
              onClick={handleAdd}
              disabled={adding || !selectedUserId}
              data-testid="add-member-submit"
            >
              {adding ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Adding...
                </>
              ) : (
                'Add Member'
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
};

export default ProjectMembersPanel;
